import { paths } from 'src/routes/paths';

import SafeStorage from 'src/utils/safe-storage';
import { JWT_KEY, USER_KEY } from 'src/utils/constant';
import axios, { newAxiosInstance } from 'src/utils/axios';

import { CONFIG } from 'src/config-global';

// ----------------------------------------------------------------------

export function jwtDecode(token) {
  try {
    if (!token) return null;

    const parts = token.split('.');
    if (parts.length < 2) {
      throw new Error('Invalid token!');
    }

    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const decoded = JSON.parse(atob(base64));

    return decoded;
  } catch (error) {
    console.error('Error decoding token:', error);
    throw error;
  }
}

// ----------------------------------------------------------------------

export function isValidToken(accessToken) {
  if (!accessToken) {
    return false;
  }

  try {
    const decoded = jwtDecode(accessToken);

    if (!decoded || !('exp' in decoded)) {
      return false;
    }

    const currentTime = Date.now() / 1000;

    return decoded.exp > currentTime;
  } catch (error) {
    console.error('Error during token validation:', error);
    return false;
  }
}

// ----------------------------------------------------------------------

export function tokenExpired(exp) {
  const currentTime = Date.now();
  const timeLeft = exp * 1000 - currentTime;

  setTimeout(() => {
    try {
      alert('Token expired!');
      SafeStorage.clearSession();
      if (typeof window !== 'undefined') {
        window.location.href = paths.auth.jwt.signIn;
      }
    } catch (error) {
      console.error('Error during token expiration:', error);
      throw error;
    }
  }, timeLeft);
}

// ----------------------------------------------------------------------

export async function setSession(accessToken, agent) {
  try {
    if (accessToken) {
      SafeStorage.setSessionItem(JWT_KEY, accessToken);

      // Set Authorization header globally
      axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
      newAxiosInstance.defaults.headers.common.Authorization = `Bearer ${accessToken}`;

      // Optional: Set default agent param globally
      if (agent) {
        axios.defaults.params = {
          ...(axios.defaults.params || {}),
          agent,
          domain: CONFIG.appDomain,
        };
        newAxiosInstance.defaults.params = {
          ...(newAxiosInstance.defaults.params || {}),
          agent,
          domain: CONFIG.appDomain,
        };
      }

      // Decode and handle token expiry
      const decodedToken = jwtDecode(accessToken);
      if (decodedToken && 'exp' in decodedToken) {
        tokenExpired(decodedToken.exp);
      } else {
        throw new Error('Invalid access token!');
      }
    } else {
      // Clear session when no token provided
      clearSession();
    }
  } catch (error) {
    console.error('Error during set session:', error);
    throw error;
  }
}

// ----------------------------------------------------------------------

export function clearSession() {
  try {
    // Clear storage
    SafeStorage.removeSessionItem(JWT_KEY);
    SafeStorage.removeSessionItem(USER_KEY);

    // Clear axios headers
    delete axios.defaults.headers.common.Authorization;
    delete newAxiosInstance.defaults.headers.common.Authorization;

    // Clear axios default params
    axios.defaults.params = {};
    newAxiosInstance.defaults.params = {};
  } catch (error) {
    console.error('Error during clear session:', error);
  }
}

export async function setUserSession(user) {
  try {
    if (user) {
      SafeStorage.setSessionJSON(USER_KEY, user);
    } else {
      SafeStorage.removeSessionItem(USER_KEY);
    }
  } catch (error) {
    console.error('Error during set session:', error);
    throw error;
  }
}

export const getAccessToken = () => SafeStorage.getSessionItem(JWT_KEY) || null;
export const getUserDetails = () => SafeStorage.getSessionJSON(USER_KEY) || null;

export const updateUserSession = (payload = {}) => {
  try {
    const user = getUserDetails() || {};
    const updatedUserData = { ...user, ...payload };
    setUserSession(updatedUserData);
  } catch (error) {
    console.error('Error during update user session:', error);
    throw error;
  }
};
